/**
 * Component for rendering filtering and sorting forms
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
import React, { PropTypes } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import FiltersForm from 'common/components/filters_form';
import SortingForm from 'common/components/sorting_form';
import { getFiltersString, cleanupFilters, getFormattedFilters, getFiltersObject } from 'common/utils';
import queryString from 'query-string';

/**
 * Returns filtering form component wrapped in redux-form and
 * with initial values loaded
 *
 * @param {string} page - page name
 * @param {object} params - parameters for the form
 * @returns {node} form - wrapped filtering form component
 */
export const getFilteringForm = (page, params) => {
  const { onSubmit, filters, appliedFilteringFn,
    onNotifyFilterForm, disabled, location, push } = params;
  const formName = `${page}-filtering-form`;

  const onFilterChange = (fieldName, value) => {
    onNotifyFilterForm(fieldName, value);
  };

  const mapStateToProps = (state) => {
    const appliedFiltering = appliedFilteringFn(state, formName);
    const routeFilters = getFiltersObject(queryString.parse(location.search).filters);
    return {
      initialValues: {
        ...appliedFiltering,
        ...getFormattedFilters(routeFilters, formName),
      },
    };
  };

  let WrappedFilteringForm = reduxForm({
    form: formName,
    onSubmit,
    onSubmitSuccess: (results, dispatch, form) => {
      const newFilters = getFiltersString(cleanupFilters(form.values));
      const searches = queryString.parse(location.search);
      const search = queryString.stringify({ ...searches, filters: newFilters });
      push({
        pathname: location.pathname,
        search: `?${search}`,
      });
    },
  })(FiltersForm);
  WrappedFilteringForm = connect(mapStateToProps)(WrappedFilteringForm);

  return (
    <WrappedFilteringForm
      onChange={onFilterChange}
      disabled={disabled}
      formName={formName}
      filters={filters}
    />
  );
};

/**
 * Returns sorting form component wrapped in redux-form and
 * with initial values loaded
 *
 * @param {string} page - page name
 * @param {object} params - parameters for the form
 * @returns {node} form - wrapped sorting form component
 */
export const getSortingForm = (page, params) => {
  const { onSubmit, sortingOptions, appliedSortingFn, disabled } = params;
  const formName = `${page}-sorting-form`;

  const mapStateToProps = (state) => {
    const appliedSorting = appliedSortingFn(state, formName);

    return {
      initialValues: { ...appliedSorting },
    };
  };

  let WrappedSortingForm = reduxForm({
    form: formName,
  })(SortingForm);
  WrappedSortingForm = connect(mapStateToProps)(WrappedSortingForm);

  return (
    <WrappedSortingForm
      onChange={onSubmit}
      disabled={disabled}
      sortingOptions={sortingOptions}
      formName={formName}
    />
  );
};

/*
 * This is a separate component since I'm not sure if we will
 * have sorting/searching in the same form. Probably not.
 */
const ListControls = (props) => {
  const { sorting, filters, disabled, onNotifyFilterForm, onApplyFilters,
    appliedFilteringFn, onApplySorting, appliedSortingFn, location, router } = props;
  const sortingForm = getSortingForm(props.page, {
    onSubmit: onApplySorting,
    sortingOptions: sorting,
    appliedSortingFn,
  });

  const filteringForm = getFilteringForm(props.page, {
    onNotifyFilterForm,
    onSubmit: onApplyFilters,
    disabled,
    filters,
    appliedFilteringFn,
    location,
    push: router.push,
  });

  return (
    <div className="row list-controls">
      { sorting && sorting.length ? sortingForm : null }
      { filters && filters.length ? filteringForm : null }
    </div>
  );
};

/**
 * PropTypes.
 *
 * @property {string} page - name of the page element is inserted on
 * @property {arary} filters - array of filters to render
 * @property {arary} sorting - array of sorting options to render
 * @property {bool} disabled - boolean value that sets the form fields to disabled
 * @property {func} onNotifyFilterForm - called when filter is changed
 * @property {func} onApplyFilters -called when filter form is submitted
 * @property {func} onApplySorting - called when sorting form is submitted
 * @property {func} appliedSortingFn - function to call when submitting sorting form
 * @property {func} appliedFilteringFn - function to call when submitting filters form
 */
ListControls.propTypes = {
  page: PropTypes.string,
  filters: PropTypes.array,
  sorting: PropTypes.array,
  disabled: PropTypes.bool,
  onNotifyFilterForm: PropTypes.func,
  onApplyFilters: PropTypes.func,
  onApplySorting: PropTypes.func,
  appliedSortingFn: PropTypes.func,
  appliedFilteringFn: PropTypes.func,
};

export default withRouter(ListControls);



// WEBPACK FOOTER //
// ./common/containers/list_controls.jsx